import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "components/card";
import {
  MdArrowBack,
  MdDelete,
  MdOutlineReceipt,
  MdPlayArrow,
  MdStop,
  MdDownload,
} from "react-icons/md";
import { getAuthToken } from "utils/auth";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const LOGS_PAGE_SIZE = 20;
const DEFAULT_COST_PER_EPOCH = 1;

const formatDate = (value) => {
  if (!value) return "—";
  const normalized = value.replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const costPerEpochForModel = (modelId, modelName) => {
  const id = Number(modelId);
  if (id === 1) return 1; // gemma-3-270m
  if ((modelName || "").toLowerCase().includes("gemma-3-270m")) return 1;
  return DEFAULT_COST_PER_EPOCH;
};

const formatDollars = (value) => `$${(Number(value) || 0).toFixed(2)}`;

const statusBadgeClass = (status) => {
  const normalized = (status ?? "").toString().trim().toLowerCase();
  switch (normalized) {
    case "queued":
    case "running":
    case "in_progress":
      return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-100";
    case "success":
    case "successful":
    case "succeeded":
    case "completed":
    case "complete":
    case "done":
    case "finished":
      return "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-100";
    case "failed":
    case "fail":
    case "cancelled":
    case "canceled":
    case "error":
      return "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-100";
    default:
      return "bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-white";
  }
};

const TaskDetails = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [datasetFiles, setDatasetFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPage, setLogsPage] = useState(0);
  const [logsEnd, setLogsEnd] = useState(false);
  const [actionError, setActionError] = useState("");
  const [startLoading, setStartLoading] = useState(false);
  const [balance, setBalance] = useState(null);
  const [balanceStatus, setBalanceStatus] = useState("idle"); // idle | loading | error
  const [ggufFile, setGgufFile] = useState(null);
  const [ggufDownloading, setGgufDownloading] = useState(false);

  const datasetConfigId = useMemo(() => {
    if (!task) return null;
    return (
      task?.task?.dataset_config_id ||
      task?.dataset_config_id ||
      task?.dataset_config?.id ||
      null
    );
  }, [task]);

  const normalizeStatus = (s) => {
    const val = (s ?? "draft").toString().trim().toLowerCase();
    if (["completed", "complete", "success", "successful", "succeeded", "done", "finished"].includes(val)) {
      return "succeeded";
    }
    if (["error", "fail", "failed"].includes(val)) return "failed";
    return val || "draft";
  };
  const status = normalizeStatus(
    task?.task_status || task?.task?.status || task?.status || "draft"
  );
  const isDraft = status === "draft";
  const canStart = isDraft;
  const canStop = status === "queued";
  const canDelete = isDraft || status === "cancelled";
  const canEdit = isDraft;
  const canViewLogs = !isDraft;

  const fetchTask = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setError("You need to sign in to view this task.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      // No single-task endpoint; fetch and filter.
      const res = await fetch(`${API_BASE_URL}/tasks?limit=200&offset=0`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const reason =
          data?.detail || data?.reason || data?.message || "Unable to load task.";
        throw new Error(reason);
      }
      const items = Array.isArray(data?.items) ? data.items : [];
      const found = items.find(
        (entry) =>
          (entry.task?.id || entry.task?.task_id || entry.id || entry.task_id) ===
          Number(taskId)
      );
      if (!found) {
        throw new Error("Task not found.");
      }
      setTask(found);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to load task right now.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  const fetchDatasetFiles = useCallback(async (datasetConfigIdParam) => {
    const token = getAuthToken();
    if (!token || !datasetConfigIdParam) return;
    try {
      const res = await fetch(`${API_BASE_URL}/dataset-configs/${datasetConfigIdParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        return;
      }
      setDatasetFiles(Array.isArray(data?.files) ? data.files : []);
      if (data?.task_status || data?.task_status_message || data?.dataset_config) {
        setTask((prev) => {
          if (!prev) return prev;
          const existingTask = prev.task || {};
          const mergedTask = {
            ...existingTask,
            status: data?.task_status ?? existingTask.status,
            status_message: data?.task_status_message ?? existingTask.status_message,
            dataset_config_id: datasetConfigIdParam,
          };
          const mergedDatasetConfig = data?.dataset_config || prev.dataset_config;
          return {
            ...prev,
            task_status: data?.task_status ?? prev.task_status,
            task_status_message: data?.task_status_message ?? prev.task_status_message,
            dataset_config: mergedDatasetConfig,
            task: mergedTask,
          };
        });
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchTask();
  }, [fetchTask]);

  useEffect(() => {
    if (datasetConfigId) {
      fetchDatasetFiles(datasetConfigId);
    }
  }, [datasetConfigId, fetchDatasetFiles]);

  const fetchBalance = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setBalance(null);
      setBalanceStatus("idle");
      return;
    }
    setBalanceStatus("loading");
    try {
      const res = await fetch(`${API_BASE_URL}/balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || typeof data?.balance_dollars !== "number") {
        throw new Error("Unable to load balance");
      }
      setBalance(data.balance_dollars);
      setBalanceStatus("idle");
    } catch (e) {
      setBalance(null);
      setBalanceStatus("error");
    }
  }, []);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  const balanceLabel = useMemo(() => {
    if (balanceStatus === "loading") return "Loading...";
    if (balanceStatus === "error") return "Unavailable";
    if (typeof balance === "number") return formatDollars(balance);
    return "Unknown";
  }, [balance, balanceStatus]);

  const fetchGgufFile = useCallback(
    async (fileId) => {
      const token = getAuthToken();
      if (!token || !fileId) return;
      try {
        const res = await fetch(`${API_BASE_URL}/files?limit=200&offset=0`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        const found = items.find(
          (f) =>
            Number(f.id) === Number(fileId) &&
            (f.file_type || "").toLowerCase() === "gguf"
        );
        if (found) setGgufFile(found);
      } catch (e) {
        // ignore
      }
    },
    []
  );

  const performAction = useCallback(
    async (action) => {
      const token = getAuthToken();
      if (!token) {
        setActionError("You need to sign in first.");
        return;
      }
      if (action === "start" && balanceInsufficient) {
        setActionError(
          `Insufficient balance (${formatDollars(
            balance || 0
          )}) for expected cost ${formatDollars(expectedCost)}.`
        );
        return;
      }
      setActionError("");
      try {
        let url = "";
        let method = "POST";
        if (action === "start") url = `${API_BASE_URL}/tasks/${taskId}/start`;
        if (action === "stop") url = `${API_BASE_URL}/tasks/${taskId}/stop`;
        if (action === "delete") {
          url = `${API_BASE_URL}/tasks/${taskId}`;
          method = "DELETE";
        }
        const headers = { Authorization: `Bearer ${token}` };
        if (method === "POST") {
          headers["Content-Type"] = "application/json";
        }
        if (action === "start") setStartLoading(true);
        const res = await fetch(url, {
          method,
          headers,
          body: method === "POST" ? JSON.stringify({}) : undefined,
        });
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          const reason =
            data?.detail || data?.reason || data?.message || "Action failed.";
          throw new Error(reason);
        }
        if (action === "delete") {
          navigate("/admin/default", { replace: true });
          return;
        }
        await fetchTask();
        if (datasetConfigId) {
          await fetchDatasetFiles(datasetConfigId);
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Unable to perform action right now.";
        setActionError(message);
      } finally {
        if (action === "start") setStartLoading(false);
      }
    },
    [datasetConfigId, fetchDatasetFiles, fetchTask, navigate, taskId]
  );

  const fetchLogs = useCallback(
    async (page = 0) => {
      if (!canViewLogs) return;
      const token = getAuthToken();
      if (!token) return;
      setLogsLoading(true);
      setLogsEnd(false);
      try {
        const res = await fetch(
          `${API_BASE_URL}/tasks/${taskId}/logs?limit=${LOGS_PAGE_SIZE}&offset=${
            page * LOGS_PAGE_SIZE
          }`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new Error();
        const items = Array.isArray(data?.items) ? data.items : [];
        setLogs(items);
        setLogsEnd(items.length < LOGS_PAGE_SIZE);
        setLogsPage(page);
      } catch (e) {
        setLogs([]);
      } finally {
        setLogsLoading(false);
      }
    },
    [canViewLogs, taskId]
  );

  useEffect(() => {
    if (canViewLogs) {
      fetchLogs(0);
    }
  }, [canViewLogs, fetchLogs, status]);

  const taskMeta = useMemo(() => {
    const base = task?.base_model || {};
    const cfg = task?.dataset_config || {};
    const t = task?.task || task || {};
    return { base, cfg, t };
  }, [task]);

  const primaryGgufId = useMemo(() => {
    const direct = task?.primary_gguf_file_id || task?.primary_gguf_file?.id;
    const nested = taskMeta.t.primary_gguf_file_id || taskMeta.t.primary_gguf_file?.id;
    return direct || nested || null;
  }, [task, taskMeta]);

  const costPerEpoch = useMemo(() => {
    const baseModelId = taskMeta.base.id || taskMeta.t.base_model_id;
    return costPerEpochForModel(
      baseModelId,
      taskMeta.base.model_name || taskMeta.base.display_name
    );
  }, [taskMeta.base.display_name, taskMeta.base.id, taskMeta.base.model_name, taskMeta.t.base_model_id]);
  const expectedCost = useMemo(() => {
    const epochs = Number(taskMeta.t.epochs) || 0;
    return epochs * costPerEpoch;
  }, [costPerEpoch, taskMeta.t.epochs]);
  const balanceInsufficient =
    typeof balance === "number" && expectedCost > balance;

  useEffect(() => {
    const direct = task?.primary_gguf_file || taskMeta.t.primary_gguf_file;
    if (direct && (direct.file_type || "").toLowerCase() === "gguf") {
      setGgufFile(direct);
      return;
    }
    if (status === "succeeded" && primaryGgufId) {
      fetchGgufFile(primaryGgufId);
    }
  }, [fetchGgufFile, primaryGgufId, status, task, taskMeta]);

  const downloadFile = async (fileId) => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/files/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setActionError("Unable to download file right now.");
    }
  };

  const downloadGgufFile = async () => {
    if (!ggufFile?.id) return;
    const token = getAuthToken();
    if (!token) {
      setActionError("You need to sign in first.");
      return;
    }
    setActionError("");
    setGgufDownloading(true);

    const filenameFromHeaders = (res) => {
      const disposition = res.headers.get("Content-Disposition") || "";
      const match =
        disposition.match(/filename\*=UTF-8''([^;]+)|filename=\"?([^\";]+)\"?/i);
      const raw = match?.[1] || match?.[2] || "";
      const cleaned = raw.replace(/['"]/g, "");
      if (!cleaned) return "";
      try {
        return decodeURIComponent(cleaned);
      } catch (e) {
        return cleaned;
      }
    };
    const ensureZipFilename = (name) => {
      const base = name || (ggufFile.id ? `file-${ggufFile.id}` : "download");
      if (base.toLowerCase().endsWith(".zip")) return base;
      const trimmed = base.replace(/\.gguf$/i, "").replace(/\.$/, "");
      return `${trimmed}.zip`;
    };

    try {
      const res = await fetch(`${API_BASE_URL}/files/${ggufFile.id}/download-gguf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filename =
        filenameFromHeaders(res) ||
        ggufFile.original_name ||
        ggufFile.storage_key ||
        "";
      if (filename) {
        link.download = ensureZipFilename(filename);
      } else {
        link.download = ensureZipFilename("");
      }
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setActionError("Unable to download file right now.");
    } finally {
      setGgufDownloading(false);
    }
  };

  const renderFiles = () => {
    if (datasetFiles.length === 0) {
      return (
        <p className="text-sm font-medium text-gray-600 dark:text-gray-200">
          No dataset files available.
        </p>
      );
    }
    return (
      <div className="mt-2 space-y-2">
        {datasetFiles.map((item) => {
          const file = item.file || {};
          const name = file.original_name || file.storage_key || `File #${file.id}`;
          return (
            <div
              key={`${item.dataset_config_id}-${item.split_role}-${file.id}`}
              className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-white/10"
            >
              <div>
                <p className="font-semibold text-navy-700 dark:text-white">
                  {item.split_role} • {name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-300">
                  File ID: {file.id}
                </p>
              </div>
              <button
                type="button"
                onClick={() => downloadFile(file.id)}
                className="linear inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-700 transition duration-200 hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/10"
              >
                <MdDownload />
                Download
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="mt-5">
      <div className="mb-4 flex items-center gap-3 text-sm font-semibold text-gray-600 dark:text-gray-200">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="linear inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 transition duration-200 hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/10"
        >
          <MdArrowBack />
          Back
        </button>
        <span>Task #{taskId}</span>
      </div>

      <div className="grid grid-cols-1 gap-5">
        <Card extra="p-6">
          {loading ? (
            <p className="text-sm font-semibold text-gray-600 dark:text-gray-200">
              Loading task...
            </p>
          ) : error ? (
            <p className="text-sm font-semibold text-red-500">{error}</p>
          ) : (
            <>
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm font-semibold uppercase text-gray-500">
                    Task
                  </p>
                  <p className="text-xl font-bold text-navy-700 dark:text-white">
                    {taskMeta.t.project_name || `Task #${taskId}`}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Base: {taskMeta.base.display_name || taskMeta.base.model_name || "—"}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Dataset: {taskMeta.cfg.name || "—"}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-2">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClass(
                        status
                      )}`}
                    >
                      {status || "draft"}
                    </span>
                    {canStart && (
                      <button
                        type="button"
                        onClick={() => performAction("start")}
                        disabled={startLoading || balanceInsufficient}
                        className="linear flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-gray-600 transition duration-200 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:text-white dark:hover:bg-white/10"
                        title="Start task"
                      >
                        {startLoading ? (
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-transparent" />
                        ) : (
                          <MdPlayArrow />
                        )}
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => performAction("start")}
                      disabled={!canStart || startLoading || balanceInsufficient}
                      className="linear inline-flex items-center gap-1 rounded-lg bg-emerald-500 px-3 py-2 text-xs font-semibold text-white transition duration-200 hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {startLoading ? (
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/70 border-t-transparent" />
                      ) : (
                        <>
                          <MdPlayArrow />
                          Start
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => performAction("stop")}
                      disabled={!canStop}
                      className="linear inline-flex items-center gap-1 rounded-lg bg-amber-500 px-3 py-2 text-xs font-semibold text-white transition duration-200 hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <MdStop />
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => performAction("delete")}
                      disabled={!canDelete}
                      className="linear inline-flex items-center gap-1 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-600 transition duration-200 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-red-900/30 dark:text-red-200 dark:hover:bg-red-900/50"
                    >
                      <MdDelete />
                      Delete
                    </button>
                  </div>
                  {actionError && (
                    <p className="text-xs font-semibold text-red-500">{actionError}</p>
                  )}
                  <div className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                    Expected cost:{" "}
                    <span
                      className={balanceInsufficient ? "text-red-500 dark:text-red-300" : ""}
                    >
                      {formatDollars(expectedCost)}
                    </span>
                    <> • Balance: {balanceLabel}</>
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded-xl border border-gray-100 p-3 dark:border-white/10">
                  <p className="text-sm font-semibold text-gray-700 dark:text-white">
                    Details
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Created: {formatDate(taskMeta.t.created_at)}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Updated: {formatDate(taskMeta.t.updated_at || taskMeta.t.finished_at)}
                  </p>
                </div>

                <div className="rounded-xl border border-gray-100 p-3 dark:border-white/10">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-700 dark:text-white">
                      Logs
                    </p>
                    <button
                      type="button"
                      onClick={() => fetchLogs(logsPage)}
                      disabled={!canViewLogs}
                      className="text-xs font-semibold text-brand-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-brand-200"
                    >
                      Refresh
                    </button>
                  </div>
                  {!canViewLogs ? (
                    <p className="text-xs text-gray-500 dark:text-gray-300">
                      Logs are available after the task leaves draft.
                    </p>
                  ) : logsLoading ? (
                    <p className="text-sm font-semibold text-gray-600 dark:text-gray-200">
                      Loading logs...
                    </p>
                  ) : logs.length === 0 ? (
                    <p className="text-sm font-semibold text-gray-600 dark:text-gray-200">
                      No logs yet.
                    </p>
                  ) : (
                    <div className="mt-2 max-h-52 overflow-y-auto rounded-lg border border-gray-100 dark:border-white/10">
                      <ul className="divide-y divide-gray-100 dark:divide-white/10">
                        {logs.map((log) => (
                          <li key={log.id || `${log.timestamp}-${log.message}`} className="px-3 py-2">
                            <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-300">
                              {formatDate(log.timestamp || log.created_at)} {log.level ? `• ${log.level}` : ""}
                            </p>
                            <p className="text-sm text-gray-800 dark:text-white">
                              {log.message || log.detail || "—"}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {canViewLogs && (
                    <div className="mt-2 flex items-center justify-between text-xs font-semibold text-gray-600 dark:text-gray-300">
                      <button
                        type="button"
                        onClick={() => fetchLogs(Math.max(0, logsPage - 1))}
                        disabled={logsPage === 0 || logsLoading}
                        className="rounded-lg border border-gray-200 px-2 py-1 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:hover:bg-white/10"
                      >
                        Prev
                      </button>
                      <span>Page {logsPage + 1}</span>
                      <button
                        type="button"
                        onClick={() => fetchLogs(logsPage + 1)}
                        disabled={logsLoading || logsEnd}
                        className="rounded-lg border border-gray-200 px-2 py-1 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:hover:bg-white/10"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {status === "succeeded" && (
                <div className="mt-4 rounded-xl border border-gray-100 p-3 dark:border-white/10">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-700 dark:text-white">
                      Model artifact (GGUF)
                    </p>
                    {ggufFile && (
                      <button
                        type="button"
                        onClick={downloadGgufFile}
                        disabled={ggufDownloading}
                        className="linear inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-700 transition duration-200 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:text-white dark:hover:bg-white/10"
                      >
                        {ggufDownloading ? (
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-transparent" />
                        ) : (
                          <MdDownload />
                        )}
                        {ggufDownloading ? "Preparing..." : "Download"}
                      </button>
                    )}
                  </div>
                  {!ggufFile ? (
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      No GGUF artifact available yet.
                    </p>
                  ) : (
                    <div className="mt-2 text-sm text-gray-700 dark:text-white">
                      <p className="font-semibold">{ggufFile.original_name || ggufFile.storage_key || `File #${ggufFile.id}`}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-300">
                        File ID: {ggufFile.id} • Size: {Math.round((ggufFile.size_bytes || 0) / 1024)} KB
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 rounded-xl border border-gray-100 p-3 dark:border-white/10">
                <p className="text-sm font-semibold text-gray-700 dark:text-white">
                  Dataset files
                </p>
                {!canEdit && (
                  <p className="text-xs text-gray-500 dark:text-gray-300">
                    Files are read-only once the task leaves draft.
                  </p>
                )}
                {renderFiles()}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default TaskDetails;
